import PropTypes from 'prop-types';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AppBar, Toolbar } from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import React from 'react';

// auth
import { useAuthContext } from 'src/auth/hooks';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
import useBrandName from 'src/hooks/use-brand-name';
// theme
import { bgGradient } from 'src/theme/css';
// components
import Logo from 'src/components/logo';
import { pxToRem } from 'src/theme/typography';

// ----------------------------------------------------------------------

const METHODS = [
  {
    id: 'jwt',
    label: 'Jwt',
    path: paths.auth.jwt.login,
    icon: '/assets/icons/auth/ic_jwt.svg',
  },
  {
    id: 'firebase',
    label: 'Firebase',
    path: paths.auth.firebase.login,
    icon: '/assets/icons/auth/ic_firebase.svg',
  },
  {
    id: 'amplify',
    label: 'Amplify',
    path: paths.auth.amplify.login,
    icon: '/assets/icons/auth/ic_amplify.svg',
  },
  {
    id: 'auth0',
    label: 'Auth0',
    path: paths.auth.auth0.login,
    icon: '/assets/icons/auth/ic_auth0.svg',
  },
];

const b2cCarouselSlides = [
  {
    image: '/assets/carousel/rapid_process.webp',
    title: 'Rapid Online Process',
    description:
      'Users can apply for visas to over 20 countries in just 5 minutes, significantly reducing the time and effort traditionally associated with visa applications.',
  },
  {
    image: '/assets/carousel/success_rate.webp',
    title: 'High Success Rate & Timely Processing',
    description:
      'Tryvisa boasts a 98.5% success rate and guarantees on-time visa processing, ensuring reliability and peace of mind for travelers.',
  },
  {
    image: '/assets/carousel/countries_supported.webp',
    title: '40+ countries supported',
    description:
      'Easily manage visa requirements for over 40 countries and beyond with our all-in-one travel visa app. Get real-time updates, application support, and global coverage for every destination.',
  },
];

const agentCarouselSlides = [
  {
    image: '/assets/carousel/track_manage_apply.webp',
    title: 'Track, Manage, and Apply Client Visas Faster',
    description:
      'Monitor all client applications, submit documents, and receive updates in one dashboard. Simplify approvals and streamline workflows.',
  },
  {
    image: '/assets/carousel/visa_vouchers.webp',
    title: 'Visa Vouchers – Simple and Clean',
    description:
      'Create straightforward visa vouchers with only the necessary details—no extras, easily add markup in each visa booking.',
  },
  {
    image: '/assets/carousel/custom_branding.webp',
    title: 'Custom Branding and Pricing',
    description:
      'Create white-labeled vouchers featuring your branding, company details and pricing for a professional, seamless client experience.',
  },
];

export default function AuthClassicLayout({ children, image, title }) {
  const { method } = useAuthContext();
  const { isAgent } = useBrandName();

  const theme = useTheme();
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const sliderRef = React.useRef(null);

  const upMd = useResponsive('up', 'md');
  const downSm = useResponsive('down', 'sm');

  const carouselSlides = isAgent ? agentCarouselSlides : b2cCarouselSlides;

  const handleSlideChange = (index) => {
    setCurrentSlide(index);
  };

  const goToSlide = (index) => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index);
    }
  };

  const carouselSettings = {
    dots: false, // Disable default dots
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 4000,
    beforeChange: (oldIndex, newIndex) => {
      setCurrentSlide(newIndex);
    },
  };

  const renderHeader = (
    <AppBar
      position="fixed" // Fixes the header to the top
      sx={{
        backgroundColor: '#FFFFFF',
        boxShadow: 'none',
      }}
    >
      <Toolbar>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <Logo />
        </Box>
      </Toolbar>
    </AppBar>
  );

  const renderContent = (
    <Stack
      flexGrow={1}
      sx={{
        width: 1,
        maxWidth: '100%',
        px: { xs: 0, md: 0 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: downSm ? 'flex-start' : 'center',
        pt: downSm ? 20 : 0,
        position: 'relative',
        overflow: 'hidden', // Prevent content overflow
      }}
    >
      <Box
        component="img"
        alt="auth"
        src="/assets/background/world.svg"
        sx={{
          width: '90%',
          objectFit: 'cover',
          filter: 'brightness(0.7)',
          position: 'absolute',
          top: '0%', // Position above the text
          right: '0%',
          zIndex: '-10',
        }}
      />
      <Link component={RouterLink} href="/" sx={{ display: 'contents' }}>
        <Box
          component="img"
          src={isAgent ? '/logo/logo_agent_single.svg' : '/logo/logo.svg'}
          sx={{ width: downSm ? '160px' : '200px', mb: 2 }}
        />
      </Link>
      {children}
    </Stack>
  );

  const renderSection = (
    <Stack
      flexGrow={1}
      alignItems="center"
      justifyContent="center"
      spacing={10}
      sx={{
        backgroundColor: theme.palette.background.secondary,
        width: '100%',
        overflow: 'hidden', // Prevent section overflow
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: { xs: 300, md: 400, lg: 650, xl: 800 },
          mx: 'auto',
          py: 6,
          overflow: 'hidden', // Prevent carousel overflow
        }}
      >
        <Slider {...carouselSettings} ref={sliderRef}>
          {carouselSlides.map((slide, idx) => (
            <Box
              key={idx}
              sx={{
                outline: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 420,
                px: 2,
                '&:focus': {
                  outline: 'none',
                },
              }}
            >
              <Box
                component="img"
                src={slide.image}
                alt={slide.title}
                sx={{
                  width: { xs: 260, md: 350, lg: 600 },
                  height: 'auto',
                  mb: 3,
                  display: 'block',
                  margin: '0 auto',
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.contrastText',
                  mb: 1,
                  textAlign: 'center',
                  textShadow: '1px 1px 4px rgba(0,0,0,0.3)',
                }}
              >
                {slide.title}
              </Typography>
              <Typography
                variant="body1"
                textAlign="center"
                sx={{
                  color: 'primary.contrastText',
                  opacity: 0.85,
                  textAlign: 'center',
                }}
              >
                {slide.description}
              </Typography>
            </Box>
          ))}
        </Slider>

        {/* Custom Dots */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: 3,
            gap: 1,
          }}
        >
          {carouselSlides.map((_, index) => (
            <Box
              key={index}
              onClick={() => goToSlide(index)}
              sx={{
                width: currentSlide === index ? 16 : 12,
                height: currentSlide === index ? 16 : 12,
                borderRadius: '50%',
                backgroundColor:
                  currentSlide === index ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.4)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow:
                  currentSlide === index ? `0 0 8px ${theme.palette.primary.main}40` : 'none',
                '&:hover': {
                  backgroundColor:
                    currentSlide === index
                      ? theme.palette.primary.main
                      : 'rgba(255, 255, 255, 0.7)',
                  transform: 'scale(1.1)',
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Stack>
  );

  return (
    <Stack
      component="main"
      direction="row"
      sx={{
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden', // Prevent horizontal scroll
      }}
    >
      {/* {renderHeader} */}

      {upMd && renderSection}

      {renderContent}
    </Stack>
  );
}

AuthClassicLayout.propTypes = {
  children: PropTypes.node,
  image: PropTypes.string,
  title: PropTypes.string,
};
